import React, { useEffect, useState } from 'react'
import { useShortenedLabels } from 'src/Libs/downloads/hooks/use-shortened-titles'
import { DownloadsSearchResultParent } from 'src/Libs/downloads/models'
import { BreadcrumbsLayout } from '../BreadcrumbsLayout'
import { getItemName, goToDirectParent } from '../../utils'
import { SxProps, useTheme } from '@mui/material'
import { useCategoryDisplayName } from '../../hooks'
import { Variant } from '@mui/material/styles/createTypography'

interface Props {
  parentCategories: DownloadsSearchResultParent[]
  onClickCategory: (id: number) => void
  variant?: Variant
  breakLastBreadcrumb?: boolean
  iconStyle?: SxProps
}

/**
 * Breadcrumbs component for search results on download page.
 * Will shorten breadcrumb titles if they exceed available space, beginning with the first one
 */
const DownloadItemBreadcrumbs: React.FC<Props> = ({ parentCategories, onClickCategory, variant, breakLastBreadcrumb = false, iconStyle }) => {
  const theme = useTheme()
  const [breadcrumbs, setBreadcrumbs] = useState<{ title: string; className?: string }[]>([])
  const getCategoryDisplayName = useCategoryDisplayName()

  const wrapperRef = React.useRef<HTMLDivElement>(null)
  const dotRef = React.useRef<HTMLDivElement>(null)

  const shortenTitles = useShortenedLabels({ parentCategories, iconButtonRef: undefined, wrapperRef, dotRef, contentSpacer: theme.space(1) })

  useEffect(() => {
    const lastIdx = parentCategories.length - 1

    const newBreadcrumbs = parentCategories.map((item, idx) => ({
      title: idx === lastIdx ? getItemName(item.id, getCategoryDisplayName(item.nameLanguages)) : shortenTitles[idx],
      onClick: () => onClickCategory(item.id),
    }))

    setBreadcrumbs(newBreadcrumbs)
  }, [shortenTitles, parentCategories]) // eslint-disable-line react-hooks/exhaustive-deps

  if (parentCategories.length === 0) {
    return null
  }

  return (
    <BreadcrumbsLayout
      wrapperRef={wrapperRef}
      dotRef={dotRef}
      onGoBack={() =>
        goToDirectParent(
          parentCategories.map(item => ({ id: item.id, name: getCategoryDisplayName(item.nameLanguages) })),
          onClickCategory
        )
      }
      breadcrumbs={breadcrumbs}
      variant={variant ?? 'subtitle2'}
      sx={{ width: '100%', mb: 0 }}
      breakLastBreadcrumb={breakLastBreadcrumb}
      iconStyle={iconStyle}
    />
  )
}

export default DownloadItemBreadcrumbs
